import React, { useRef, useEffect } from 'react';

// An elegant, on-brand "galactic expanse" constellation field.
// Particles drift with subtle depth (universe travel) and connect with
// thin lines echoing the HPP network mark. Teal -> gold palette. Not neon.
//
// variant="dark"  -> light stars on dark sections (default)
// variant="light" -> darker stars/lines on white sections for contrast
export default function ParticleField({ density = 1, variant = 'dark', className = '' }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];

    const isLight = variant === 'light';

    // Palettes tuned per background
    const palette = isLight
      ? ['#1f5866', '#2b6d7c', '#5f7d4f', '#6f8f5a', '#b79a3f', '#a98f3a']
      : ['#3f8593', '#5f9a86', '#8bb07a', '#c6a94b', '#d9c477', '#9fc0c4'];

    // Line + glow behaviour per variant — tuned to read as an interconnected
    // network of nodes (crisper dots, stronger links) rather than floating stars.
    const lineRGB = isLight ? '28, 82, 96' : '124, 174, 182';
    const lineMaxAlpha = isLight ? 0.34 : 0.42;
    const glowMult = isLight ? 2.4 : 3.2;
    const glowAlpha = isLight ? 0.16 : 0.32;
    const coreAlpha = isLight ? 0.95 : 1.0;
    const linkDist = isLight ? 150 : 172;
    const lineWidth = 0.85;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildParticles();
    };

    const buildParticles = () => {
      const area = width * height;
      const count = Math.max(30, Math.min(150, Math.floor((area / 12000) * density)));
      particles = new Array(count).fill(0).map(() => {
        const z = Math.random() * 0.8 + 0.2; // depth
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          z,
          vx: (Math.random() - 0.5) * 0.18 * z,
          vy: (Math.random() - 0.5) * 0.18 * z,
          r: (Math.random() * 1.7 + 0.9) * z,
          c: palette[Math.floor(Math.random() * palette.length)],
          tw: Math.random() * Math.PI * 2, // twinkle phase
        };
      });
    };

    const step = () => {
      ctx.clearRect(0, 0, width, height);
      const mx = (mouse.current.x - 0.5) * 18;
      const my = (mouse.current.y - 0.5) * 18;

      // draw connecting lines
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = (a.x + mx * a.z) - (b.x + mx * b.z);
          const dy = (a.y + my * a.z) - (b.y + my * b.z);
          const dist = Math.hypot(dx, dy);
          const max = linkDist;
          if (dist < max) {
            const alpha = (1 - dist / max) * lineMaxAlpha * ((a.z + b.z) / 2);
            ctx.strokeStyle = `rgba(${lineRGB}, ${alpha})`;
            ctx.lineWidth = lineWidth;
            ctx.beginPath();
            ctx.moveTo(a.x + mx * a.z, a.y + my * a.z);
            ctx.lineTo(b.x + mx * b.z, b.y + my * b.z);
            ctx.stroke();
          }
        }
      }

      // draw particles
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.tw += 0.02;
        if (p.x < -20) p.x = width + 20;
        if (p.x > width + 20) p.x = -20;
        if (p.y < -20) p.y = height + 20;
        if (p.y > height + 20) p.y = -20;

        const twinkle = 0.55 + Math.sin(p.tw) * 0.28;
        const px = p.x + mx * p.z;
        const py = p.y + my * p.z;

        // glow
        const grad = ctx.createRadialGradient(px, py, 0, px, py, p.r * glowMult);
        grad.addColorStop(0, hexToRgba(p.c, glowAlpha * twinkle));
        grad.addColorStop(1, hexToRgba(p.c, 0));
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(px, py, p.r * glowMult, 0, Math.PI * 2);
        ctx.fill();

        // core
        ctx.fillStyle = hexToRgba(p.c, coreAlpha * twinkle);
        ctx.beginPath();
        ctx.arc(px, py, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(step);
    };

    const hexToRgba = (hex, a) => {
      const h = hex.replace('#', '');
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${a})`;
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current.x = (e.clientX - rect.left) / rect.width;
      mouse.current.y = (e.clientY - rect.top) / rect.height;
    };

    resize();
    step();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMove);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
    };
  }, [density, variant]);

  return <canvas ref={canvasRef} className={`canvas-layer ${className}`} aria-hidden="true" />;
}
